import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { registerUser, signInWithGoogle } from '../services/auth';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { GoogleSignInButton, AuthDivider } from '../components/auth/GoogleSignInButton';
import { Store, Mail, Lock, UserPlus, AlertCircle, ShieldCheck } from 'lucide-react';

const RegisterContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const RegisterCard = styled.div`
  position: relative;
  width: 100%;
  max-width: 460px;
  padding: 32px 28px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(246, 250, 253, 0.95) 100%);
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 24px;
  overflow: hidden;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.95) inset,
    0 8px 24px -8px rgba(46, 91, 136, 0.12),
    0 24px 60px -20px rgba(46, 91, 136, 0.25);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #059669 0%, #10B981 50%, #059669 100%);
  }

  &::after {
    content: '';
    position: absolute;
    top: -30%;
    right: -20%;
    width: 260px;
    height: 260px;
    background: radial-gradient(circle, rgba(16, 185, 129, 0.12) 0%, transparent 60%);
    pointer-events: none;
  }

  & > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 24px 20px;
    border-radius: 20px;
  }
`;

const LogoMark = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 18px;
  background: linear-gradient(135deg, #059669 0%, #10B981 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.3) inset,
    0 10px 24px -6px rgba(5, 150, 105, 0.5),
    0 4px 10px -2px rgba(16, 185, 129, 0.4);

  svg {
    width: 28px;
    height: 28px;
    color: #ffffff;
    stroke-width: 2.4;
  }
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: 24px;

  h1 {
    margin: 0 0 6px;
    font-size: 28px;
    font-weight: 800;
    letter-spacing: -1px;
    background: linear-gradient(135deg, #047857 0%, #10B981 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  p {
    margin: 0;
    font-size: 13px;
    color: #64748b;
    font-weight: 500;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const GlobalError = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  margin-bottom: 10px;
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.08) 0%, rgba(220, 38, 38, 0.12) 100%);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 11px;
  color: #B91C1C;
  font-size: 13px;
  font-weight: 600;

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    stroke-width: 2.4;
  }
`;

const TrustBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 18px;
  padding: 8px 12px;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(5, 150, 105, 0.12) 100%);
  border: 1px solid rgba(16, 185, 129, 0.22);
  border-radius: 999px;
  color: #047857;
  font-size: 11.5px;
  font-weight: 600;

  svg {
    width: 13px;
    height: 13px;
    stroke-width: 2.6;
  }
`;

const LinkText = styled.p`
  text-align: center;
  margin: 20px 0 0;
  font-size: 13.5px;
  color: #64748b;
  font-weight: 500;

  a {
    color: #059669;
    font-weight: 700;
    text-decoration: none;
    background: linear-gradient(135deg, #047857 0%, #10B981 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;

    &:hover {
      text-decoration: underline;
    }
  }
`;

export const Register = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    pharmacyName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    setError('');
    try {
      await signInWithGoogle();
      navigate('/');
    } catch (err) {
      console.error('Google sign-in error:', err);
      if (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') {
      } else if (err.code === 'auth/account-exists-with-different-credential') {
        setError('Ya existe una cuenta con este correo usando otro método de inicio de sesión.');
      } else if (err.message && err.message.includes('cancel')) {
      } else {
        setError('No se pudo registrar con Google. Intenta de nuevo.');
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    if (formData.password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await registerUser(formData.email, formData.password, formData.pharmacyName);
      navigate('/');
    } catch (error) {
      console.error('Registration error:', error);
      if (error.code === 'auth/email-already-in-use') {
        setError('Ya existe una cuenta con este correo electrónico');
      } else if (error.code === 'auth/invalid-email') {
        setError('Correo electrónico inválido');
      } else if (error.code === 'auth/weak-password') {
        setError('La contraseña es muy débil');
      } else {
        setError('Error al crear la cuenta. Por favor intente nuevamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <RegisterContainer>
      <RegisterCard>
        <LogoMark>
          <UserPlus />
        </LogoMark>
        <Logo>
          <h1>Crea tu cuenta</h1>
          <p>Empieza a gestionar tu farmacia en minutos</p>
        </Logo>

        <GoogleSignInButton
          onClick={handleGoogleSignIn}
          loading={googleLoading}
          disabled={loading}
          label="Registrarse con Google"
        />

        <AuthDivider text="o regístrate con tu correo" />

        <Form onSubmit={handleSubmit}>
          {error && (
            <GlobalError>
              <AlertCircle />
              {error}
            </GlobalError>
          )}

          <Input
            label={<><Store size={12} style={{ color: '#10B981' }} /> Nombre de la Farmacia</>}
            name="pharmacyName"
            type="text"
            value={formData.pharmacyName}
            onChange={handleInputChange}
            placeholder="Ej: Farmacia San José"
            required
          />

          <Input
            label={<><Mail size={12} style={{ color: '#10B981' }} /> Correo Electrónico</>}
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder="tucorreo@ejemplo.com"
            required
          />

          <Input
            label={<><Lock size={12} style={{ color: '#10B981' }} /> Contraseña</>}
            name="password"
            type="password"
            value={formData.password}
            onChange={handleInputChange}
            placeholder="Mínimo 6 caracteres"
            required
          />

          <Input
            label={<><Lock size={12} style={{ color: '#10B981' }} /> Confirmar Contraseña</>}
            name="confirmPassword"
            type="password"
            value={formData.confirmPassword}
            onChange={handleInputChange}
            placeholder="Repite tu contraseña"
            required
          />

          <Button
            type="submit"
            variant="success"
            fullWidth
            size="large"
            disabled={loading || googleLoading}
          >
            <UserPlus size={16} style={{ marginRight: 6 }} />
            {loading ? 'Creando cuenta...' : 'Crear Cuenta'}
          </Button>
        </Form>

        <TrustBadge>
          <ShieldCheck />
          Datos seguros y encriptados
        </TrustBadge>

        <LinkText>
          ¿Ya tienes una cuenta? <Link to="/login">Inicia sesión aquí</Link>
        </LinkText>
      </RegisterCard>
    </RegisterContainer>
  );
};
